import {CloseOutlined} from '@ant-design/icons';
import {Button, Checkbox, Flex, Input, Modal, Popconfirm, Select} from 'antd';
import type {DefaultOptionType} from 'antd/es/select';
import type {TableRowSelection} from 'antd/es/table/interface';
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import {drawerZIndex} from '../../../constants/Common';
import {
  statusEm,
  statusOptions as defaultStatusOptions
} from '../../../pages/netZero/utils/commonFunctions';
import {checkerStatusLabel} from '../../Forms/Utils/checkerStatusKeyLabel';

export function excludeAcceptedIdsForDelete<
  T extends {_id: string; status?: string}
>(ids: string[], records: T[]): string[] {
  const rows = Array.isArray(records) ? records : [];
  const acceptedIds = new Set(
    rows.filter((r) => r.status === statusEm.ACCEPTED).map((r) => r._id)
  );

  return ids.filter((id) => !acceptedIds.has(id));
}

export function getBulkSelectedIdsInDataSource<T extends {_id: string}>(
  selectedRowKeys: React.Key[],
  records: T[]
): string[] {
  const keys = selectedRowKeys.filter(Boolean) as string[];
  if (!keys.length) {
    return [];
  }
  const currentIds = new Set(records.map((record) => record._id));

  return keys.filter((id) => currentIds.has(id));
}

export function createBulkRowSelection<T extends object>(options: {
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  getCheckboxProps?: (record: T) => {disabled?: boolean};
  enabled?: boolean;
}): TableRowSelection<T> | undefined {
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    onSelectAll,
    onDeselectAll,
    getCheckboxProps,
    enabled = true
  } = options;

  if (!enabled) {
    return undefined;
  }

  return {
    type: 'checkbox',
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    ...(getCheckboxProps ? {getCheckboxProps} : {}),
    onChange: (keys, _rows, info) => {
      if (info.type === 'all') {
        return;
      }
      setSelectedRowKeys(keys);
    },
    onSelectAll: (selected) => {
      if (selected) {
        onSelectAll?.();
      } else {
        onDeselectAll?.();
      }
    }
  };
}

export interface TableBulkSelectionBarProps {
  selectedCount: number;
  selectedIds: string[];
  handleDelete: (_id: string[] | string) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  allSelectableCount?: number;
  onChangeStatus?: () => void;
  onBulkDelete?: () => void;
  hideIsDelete?: boolean;
}

function TableBulkSelectionBar({
  selectedCount,
  selectedIds,
  handleDelete,
  onSelectAll,
  onDeselectAll,
  allSelectableCount,
  onChangeStatus,
  onBulkDelete,
  hideIsDelete = false
}: TableBulkSelectionBarProps): ReactElement {
  const isAllSelected =
    allSelectableCount != null &&
    allSelectableCount > 0 &&
    selectedCount >= allSelectableCount;

  const handleSelectAllClick = () => {
    if (isAllSelected) {
      onDeselectAll?.();
    } else {
      onSelectAll?.();
    }
  };

  return (
    <div className="tableBulkSelectionBar">
      <Flex
        align="center"
        justify="center"
        wrap="wrap"
        gap="small"
        className="tableBulkSelectionBar__inner"
      >
        <Flex align="center" wrap="wrap" gap="small">
          <span className="tableBulkSelectionBar__pill">
            <span className="tableBulkSelectionBar__countBadge">
              {selectedCount}
            </span>
            <span className="tableBulkSelectionBar__selectedLabel">
              selected
            </span>
          </span>
          {onSelectAll && (
            <Button
              type="text"
              className="tableBulkSelectionBar__action"
              onClick={handleSelectAllClick}
            >
              {isAllSelected ? 'Deselect all' : 'Select all'}
            </Button>
          )}
          {onChangeStatus && (
            <Button
              type="text"
              className="tableBulkSelectionBar__action"
              onClick={onChangeStatus}
            >
              Change status
            </Button>
          )}
          {!hideIsDelete &&
            (onBulkDelete ? (
              <Button
                type="text"
                className="tableBulkSelectionBar__action"
                onClick={onBulkDelete}
              >
                Delete
              </Button>
            ) : (
              <Popconfirm
                title="Sure to delete?"
                zIndex={drawerZIndex}
                onConfirm={() => {
                  handleDelete(selectedIds);
                }}
              >
                <Button type="text" className="tableBulkSelectionBar__action">
                  Delete
                </Button>
              </Popconfirm>
            ))}
          {onDeselectAll && (
            <Button
              type="text"
              className="tableBulkSelectionBar__close"
              icon={<CloseOutlined />}
              aria-label="Clear selection"
              onClick={() => {
                onDeselectAll();
              }}
            />
          )}
        </Flex>
      </Flex>
    </div>
  );
}

export default TableBulkSelectionBar;

export interface BulkStatusSubmitArgs {
  ids: string[];
  status: string;
  comment: string;
}

export interface UseBulkCheckerModalsParams {
  selectedIds: string[];
  /** When true, bulk modals close and selection is cleared (table refresh). */
  refresh?: boolean;
  /** When this value changes, bulk modals close and selection is cleared. */
  selectionContextKey?: string;
  onSelectionContextChange?: () => void;
  onSuccess: () => void;
  onBulkStatusSubmit: (args: BulkStatusSubmitArgs) => Promise<unknown>;
  onBulkDelete: (ids: string[]) => Promise<unknown>;
  getBulkDeleteEligibleCount?: (ids: string[]) => number;
  statusSelectOptions?: DefaultOptionType[];
  labels?: Partial<{
    changeStatusTitle: string;
    deleteTitle: string;
    changeStatusIntro: string;
    deleteIntro: string;
    deleteNoneEligibleIntro: string;
  }>;
  classNames?: Partial<{
    modalRoot: string;
    modalContent: string;
    modalContentDelete: string;
  }>;
}

export interface UseBulkCheckerModalsResult {
  openChangeStatus: () => void;
  openBulkDelete: () => void;
  bulkCheckerModals: ReactElement;
}

const DEFAULT_LABELS = {
  changeStatusTitle: 'Change status',
  deleteTitle: 'Delete selected records',
  changeStatusIntro: 'Update the status of',
  deleteIntro: 'You are about to delete',
  deleteNoneEligibleIntro:
    'All {count} selected records have an Accepted status and cannot be deleted.'
};

export function useBulkCheckerModals({
  selectedIds,
  refresh,
  selectionContextKey,
  onSelectionContextChange,
  onSuccess,
  onBulkStatusSubmit,
  onBulkDelete,
  getBulkDeleteEligibleCount,
  statusSelectOptions = defaultStatusOptions as DefaultOptionType[],
  labels: labelsProp,
  classNames: classNamesProp
}: UseBulkCheckerModalsParams): UseBulkCheckerModalsResult {
  const labels = {...DEFAULT_LABELS, ...labelsProp};
  const classNames = {
    modalRoot: 'fuelTableBulkModal',
    modalContent: 'fuelTableChangeStatusModal',
    modalContentDelete:
      'fuelTableChangeStatusModal fuelTableChangeStatusModal--deleteOnly',
    ...classNamesProp
  };

  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [bulkStatusSubmitting, setBulkStatusSubmitting] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<string | undefined>();
  const [bulkStatusComment, setBulkStatusComment] = useState('');
  const [bulkStatusDisclaimerAck, setBulkStatusDisclaimerAck] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
  const [deleteDisclaimerAck, setDeleteDisclaimerAck] = useState(false);

  const selectedCount = selectedIds.length;
  const bulkDeleteEligibleCount =
    getBulkDeleteEligibleCount?.(selectedIds) ?? selectedIds.length;
  const canBulkDelete = bulkDeleteEligibleCount > 0;
  const deleteNoneEligibleMessage = labels.deleteNoneEligibleIntro.replace(
    '{count}',
    String(selectedCount)
  );

  const handleBulkStatusChange = useCallback(
    (value: string | null | undefined) => {
      const next = value ?? undefined;
      setBulkStatus(next);
      if (next !== statusEm.REJECTED) {
        setBulkStatusComment('');
      }
    },
    []
  );

  const openChangeStatus = useCallback(() => {
    handleBulkStatusChange(undefined);
    setBulkStatusDisclaimerAck(false);
    setChangeStatusModalOpen(true);
  }, [handleBulkStatusChange]);

  const openBulkDelete = useCallback(() => {
    setBulkDeleteSubmitting(false);
    setDeleteDisclaimerAck(false);
    setDeleteModalOpen(true);
  }, []);

  const isBulkRejected = bulkStatus === statusEm.REJECTED;
  const bulkCommentTrimmed = bulkStatusComment.trim();
  const canSubmitBulkStatus =
    Boolean(bulkStatus) &&
    (!isBulkRejected || bulkCommentTrimmed.length > 0) &&
    bulkStatusDisclaimerAck;

  const resetChangeStatusForm = useCallback(() => {
    setBulkStatusSubmitting(false);
    setChangeStatusModalOpen(false);
    handleBulkStatusChange(undefined);
    setBulkStatusDisclaimerAck(false);
  }, [handleBulkStatusChange]);

  const handleBulkDeleteModalCancel = useCallback(() => {
    setBulkDeleteSubmitting(false);
    setDeleteModalOpen(false);
    setDeleteDisclaimerAck(false);
  }, []);

  const onSelectionContextChangeRef = useRef(onSelectionContextChange);
  onSelectionContextChangeRef.current = onSelectionContextChange;

  const handleChangeStatusClose = useCallback(() => {
    resetChangeStatusForm();
    onSelectionContextChangeRef.current?.();
  }, [resetChangeStatusForm]);

  const prevSelectionContextKeyRef = useRef(selectionContextKey);
  useEffect(() => {
    if (!selectionContextKey) {
      return;
    }

    const prev = prevSelectionContextKeyRef.current;
    prevSelectionContextKeyRef.current = selectionContextKey;

    if (prev === undefined || prev === selectionContextKey) {
      return;
    }

    resetChangeStatusForm();
    handleBulkDeleteModalCancel();
    onSelectionContextChangeRef.current?.();
  }, [selectionContextKey, resetChangeStatusForm, handleBulkDeleteModalCancel]);

  const prevRefreshRef = useRef(refresh);
  useEffect(() => {
    const prev = prevRefreshRef.current;
    prevRefreshRef.current = refresh;

    if (!refresh || prev === refresh) {
      return;
    }

    resetChangeStatusForm();
    handleBulkDeleteModalCancel();
    onSelectionContextChangeRef.current?.();
  }, [refresh, resetChangeStatusForm, handleBulkDeleteModalCancel]);

  const handleBulkStatusSubmit = useCallback(async () => {
    if (!bulkStatus || !canSubmitBulkStatus || bulkStatusSubmitting) return;
    setBulkStatusSubmitting(true);
    try {
      await onBulkStatusSubmit({
        ids: selectedIds,
        status: bulkStatus,
        comment: isBulkRejected ? bulkCommentTrimmed : ''
      });
      onSuccess();
      resetChangeStatusForm();
    } catch {
    } finally {
      setBulkStatusSubmitting(false);
    }
  }, [
    bulkStatus,
    bulkCommentTrimmed,
    canSubmitBulkStatus,
    isBulkRejected,
    bulkStatusSubmitting,
    onBulkStatusSubmit,
    onSuccess,
    resetChangeStatusForm,
    selectedIds
  ]);

  const handleBulkDeleteModalOk = useCallback(async () => {
    if (
      !deleteDisclaimerAck ||
      bulkDeleteEligibleCount === 0 ||
      bulkDeleteSubmitting
    ) {
      return;
    }
    setBulkDeleteSubmitting(true);
    try {
      const result = await onBulkDelete(selectedIds);
      if (result !== false) {
        onSuccess();
      }
      setDeleteModalOpen(false);
      setDeleteDisclaimerAck(false);
    } catch {
    } finally {
      setBulkDeleteSubmitting(false);
    }
  }, [
    bulkDeleteEligibleCount,
    bulkDeleteSubmitting,
    deleteDisclaimerAck,
    onBulkDelete,
    onSuccess,
    selectedIds
  ]);

  const bulkCheckerModals = (
    <>
      <Modal
        title={labels.changeStatusTitle}
        open={changeStatusModalOpen}
        centered
        rootClassName={classNames.modalRoot}
        zIndex={drawerZIndex}
        maskClosable={!bulkStatusSubmitting}
        closable={!bulkStatusSubmitting}
        closeIcon={<CloseOutlined />}
        onCancel={handleChangeStatusClose}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            disabled={bulkStatusSubmitting}
            onClick={resetChangeStatusForm}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={bulkStatusSubmitting}
            disabled={!canSubmitBulkStatus || bulkStatusSubmitting}
            onClick={() => {
              void handleBulkStatusSubmit();
            }}
          >
            {bulkStatusSubmitting ? 'Changing Status' : 'Submit'}
          </Button>
        ]}
      >
        <div className={classNames.modalContent}>
          <div className="fuelTableChangeStatusModal__intro">
            {labels.changeStatusIntro} <strong>{selectedCount}</strong> selected
            records.
          </div>
          <Select
            allowClear
            placeholder="Select status"
            options={statusSelectOptions}
            style={{width: '100%'}}
            value={bulkStatus}
            onChange={handleBulkStatusChange}
          />
          {isBulkRejected && (
            <>
              <div style={{margin: '16px 0 8px'}}>
                {checkerStatusLabel.COMMENT}
                <span style={{color: '#ff4d4f', marginLeft: 4}}>*</span>
              </div>
              <Input.TextArea
                required
                rows={4}
                placeholder="Enter comment"
                value={bulkStatusComment}
                maxLength={50}
                onChange={(e) => {
                  setBulkStatusComment(e.target.value);
                }}
              />
            </>
          )}
          <div className="fuelTableChangeStatusModal__disclaimer">
            <Checkbox
              checked={bulkStatusDisclaimerAck}
              onChange={(e) => {
                setBulkStatusDisclaimerAck(e.target.checked);
              }}
            />
            <span className="fuelTableChangeStatusModal__disclaimer-text">
              Are you sure you want to edit the status for the{' '}
              <strong>{selectedCount}</strong> selected records? This action
              will overwrite each record&apos;s current status.
            </span>
          </div>
        </div>
      </Modal>
      <Modal
        centered
        rootClassName={classNames.modalRoot}
        title={labels.deleteTitle}
        open={deleteModalOpen}
        zIndex={drawerZIndex}
        okText={bulkDeleteSubmitting ? 'Deleting' : 'Delete'}
        confirmLoading={bulkDeleteSubmitting}
        okButtonProps={{
          style: canBulkDelete ? undefined : {display: 'none'},
          disabled:
            !deleteDisclaimerAck || bulkDeleteSubmitting || !canBulkDelete
        }}
        cancelText={canBulkDelete ? 'Cancel' : 'Close'}
        maskClosable={!bulkDeleteSubmitting}
        closable={!bulkDeleteSubmitting}
        onCancel={handleBulkDeleteModalCancel}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onOk={handleBulkDeleteModalOk}
        destroyOnClose
      >
        <div className={classNames.modalContentDelete}>
          <div className="fuelTableChangeStatusModal__intro">
            {canBulkDelete ? (
              <>
                {labels.deleteIntro} <strong>{bulkDeleteEligibleCount}</strong>{' '}
                records. This action cannot be undone.
              </>
            ) : (
              deleteNoneEligibleMessage
            )}
          </div>
          {canBulkDelete && (
            <div className="fuelTableChangeStatusModal__disclaimer">
              <Checkbox
                checked={deleteDisclaimerAck}
                onChange={(e) => {
                  setDeleteDisclaimerAck(e.target.checked);
                }}
              />
              <span className="fuelTableChangeStatusModal__disclaimer-text">
                Yes, I understand this will permanently delete the eligible
                selected records. Records with an Accepted status will be
                skipped automatically.
              </span>
            </div>
          )}
        </div>
      </Modal>
    </>
  );

  return {
    openChangeStatus,
    openBulkDelete,
    bulkCheckerModals
  };
}
